import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { makeStyles } from "@material-ui/styles";
import {
	Card,
	CardContent,
	Typography,
	Grid,
	Box,
	IconButton,
	Collapse,
	CircularProgress,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import { safetyWiseApi } from "utils/safetyWiseApi";
import {
	AttachmentListing,
	PushPinIcon,
	TextClip,
	StyledModal,
} from "frame/components";
import {
	isNotNullOrEmpty,
	useRouter,
	getCurrentTimestamp,
	useTerminology,
} from "utils";
import { onEdit } from "features/jobs/actions";
import { CUSTOMERS_PATH } from "../actions/types";

const useStyles = makeStyles((theme) => ({
	root: {
		margin: theme.spacing(2, 2, 0, 2),
		[theme.breakpoints.down("xs")]: {
			margin: theme.spacing(1, 1.5, 0, 1.5),
		},
	},
	rootCompact: {
		margin: theme.spacing(1, 0, 0, 0),
	},
	banner: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		padding: theme.spacing(1.25, 1.5),
		border: `1px solid ${theme.palette.primary.light}`,
		borderRadius: theme.shape.borderRadius,
		backgroundColor: theme.palette.action.hover,
		cursor: "pointer",
		"&:hover": {
			backgroundColor: theme.palette.action.selected,
		},
	},
	bannerIcon: {
		color: theme.palette.primary.main,
		flexShrink: 0,
	},
	bannerText: {
		flex: 1,
		fontWeight: 500,
		color: theme.palette.text.primary,
	},
	bannerAction: {
		color: theme.palette.text.secondary,
		flexShrink: 0,
	},
	bannerLoading: {
		opacity: 0.85,
		cursor: "default",
		"&:hover": {
			backgroundColor: theme.palette.action.hover,
		},
	},
	cardList: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(2),
	},
	cardListCompact: {
		marginTop: theme.spacing(1.5),
	},
	card: {
		display: "flex",
		flexWrap: "wrap",
		backgroundColor: theme.palette.white,
		boxShadow: "none",
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: theme.spacing(1),
	},
	cardClickable: {
		cursor: "pointer",
	},
	content: {
		display: "flex",
		alignItems: "flex-start",
		gap: theme.spacing(1.25),
		padding: theme.spacing(2, 1.5, 2, 2),
		flexGrow: 1,
		width: "100%",
		"&:last-child": {
			paddingBottom: theme.spacing(2),
		},
	},
	pinButton: {
		flexShrink: 0,
		marginTop: theme.spacing(-0.5),
		marginLeft: theme.spacing(-0.5),
		color: theme.palette.primary.main,
	},
	pinIconStatic: {
		flexShrink: 0,
		marginTop: theme.spacing(0.25),
		color: theme.palette.primary.main,
	},
	cardBody: {
		flex: 1,
		minWidth: 0,
	},
	label: {
		color: theme.palette.text.tertiary,
		marginBottom: theme.spacing(0.25),
	},
	value: {
		textOverflow: "ellipsis",
		overflow: "hidden",
		wordBreak: "break-word",
	},
	thumbBlock: {
		display: "flex",
		flexWrap: "wrap",
		alignItems: "center",
		gap: theme.spacing(0.75),
		paddingTop: theme.spacing(0.25),
		"& img": {
			maxHeight: 104,
			borderRadius: 6,
			boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
		},
	},
	error: {
		...theme.typography.body2,
		color: theme.palette.error.main,
		fontWeight: 500,
	},
	loadingText: {
		...theme.typography.body2,
		color: theme.palette.text.secondary,
		fontWeight: 500,
	},
	openHint: {
		color: theme.palette.text.secondary,
		fontSize: "0.875rem",
	},
}));

const buildJobUpdateBody = (job, customerId, organisationId, pinnedToCustomer) => ({
	id: job.id,
	title: job.title || "",
	createdAt: job.createdAt,
	type: job.type || "",
	customerId: job.customerId || customerId || "",
	siteId: job.siteId || "",
	staffId: job.staffId || "",
	notes: job.notes || "",
	attachments: job.attachments || [],
	organisationId: job.organisationId || organisationId,
	latitude: job.latitude ?? "",
	longitude: job.longitude ?? "",
	timestamp: job.timestamp || getCurrentTimestamp(),
	creatorId: job.creatorId || "",
	pinnedToSite: !!job.pinnedToSite,
	pinnedToCustomer,
	pinnedToStaff: !!job.pinnedToStaff,
});

const PinnedJobCard = ({
	job,
	classes,
	customerLabel,
	allowUnpin,
	navigateOnClick,
	onUnpinClick,
	onOpenInPhotosNotes,
	onPinnedKeyDown,
}) => {
	const clickable = navigateOnClick;
	const cardProps = clickable
		? {
				onClick: () => onOpenInPhotosNotes(job),
				onKeyDown: onPinnedKeyDown(job),
				role: "button",
				tabIndex: 0,
				title: "Open in Photos / Notes",
		  }
		: {};

	return (
		<Card
			key={job.id}
			className={`${classes.card} ${clickable ? classes.cardClickable : ""}`}
			elevation={0}
			data-cy={`pinned-job-${job.id}`}
			{...cardProps}
		>
			<CardContent className={classes.content}>
				{allowUnpin ? (
					<IconButton
						className={classes.pinButton}
						size="small"
						aria-label={`Unpin from ${customerLabel}`}
						onClick={(e) => onUnpinClick(e, job)}
						data-cy={`unpin-job-${job.id}`}
					>
						<PushPinIcon fontSize="small" />
					</IconButton>
				) : (
					<PushPinIcon
						fontSize="small"
						className={classes.pinIconStatic}
						aria-hidden
					/>
				)}
				<div className={classes.cardBody}>
					{(isNotNullOrEmpty(job.title) ||
						isNotNullOrEmpty(job.notes) ||
						isNotNullOrEmpty(job.attachments)) && (
						<Grid container spacing={2}>
							{isNotNullOrEmpty(job.title) && (
								<Grid item md={4} sm={6} xs={12}>
									<Typography
										className={classes.label}
										variant="body2"
									>
										Title
									</Typography>
									<Typography
										variant="body1"
										component="div"
										className={classes.value}
									>
										<TextClip
											text={job.title}
											numberOfCharacters={200}
											hideSeeMore
										/>
									</Typography>
								</Grid>
							)}
							{isNotNullOrEmpty(job.notes) && (
								<Grid item md={4} sm={6} xs={12}>
									<Typography
										className={classes.label}
										variant="body2"
									>
										Notes
									</Typography>
									<Typography
										variant="body1"
										component="div"
										className={classes.value}
									>
										<TextClip
											text={job.notes}
											numberOfCharacters={400}
											hideSeeMore
										/>
									</Typography>
								</Grid>
							)}
							{isNotNullOrEmpty(job.attachments) && (
								<Grid item md={4} sm={6} xs={12}>
									<Typography
										className={classes.label}
										variant="body2"
									>
										Photos
									</Typography>
									<div
										className={classes.thumbBlock}
										onClick={(e) => e.stopPropagation()}
										role="presentation"
									>
										<AttachmentListing
											attachments={job.attachments}
										/>
									</div>
								</Grid>
							)}
						</Grid>
					)}
					{!isNotNullOrEmpty(job.title) &&
						!isNotNullOrEmpty(job.notes) &&
						!isNotNullOrEmpty(job.attachments) &&
						clickable && (
							<Typography
								variant="body2"
								className={classes.openHint}
							>
								Open in Photos / Notes
							</Typography>
						)}
				</div>
			</CardContent>
		</Card>
	);
};

PinnedJobCard.propTypes = {
	job: PropTypes.object.isRequired,
	classes: PropTypes.object.isRequired,
	customerLabel: PropTypes.string.isRequired,
	allowUnpin: PropTypes.bool.isRequired,
	navigateOnClick: PropTypes.bool.isRequired,
	onUnpinClick: PropTypes.func,
	onOpenInPhotosNotes: PropTypes.func,
	onPinnedKeyDown: PropTypes.func,
};

const formatPinnedLabel = (count, customerLabel) =>
	count === 1
		? `1 pinned photo / note for this ${customerLabel.toLowerCase()}`
		: `${count} pinned photos / notes for this ${customerLabel.toLowerCase()}`;

const CustomerPinnedItems = ({
	customerId,
	organisationId,
	onEditJob,
	variant = "customer",
	defaultExpanded = true,
	allowUnpin = true,
	knownPinnedCount,
}) => {
	const classes = useStyles();
	const { history } = useRouter();
	const { customerLabel } = useTerminology();
	const isCompact = variant === "compact";
	const [pinnedJobs, setPinnedJobs] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [jobToUnpin, setJobToUnpin] = useState(null);
	const [expanded, setExpanded] = useState(
		isCompact ? defaultExpanded : true
	);

	const skipFetch = knownPinnedCount === 0;

	const loadPinned = useCallback(() => {
		if (!customerId || !organisationId || skipFetch) {
			setPinnedJobs([]);
			setLoading(false);
			return;
		}
		setLoading(true);
		setError(null);
		safetyWiseApi
			.get(`${organisationId}/job/pinned-for-customer/${customerId}`)
			.then((res) => {
				setPinnedJobs(Array.isArray(res.data) ? res.data : []);
			})
			.catch(() => {
				setError("Could not load pinned items.");
				setPinnedJobs([]);
			})
			.finally(() => setLoading(false));
	}, [customerId, organisationId, skipFetch]);

	useEffect(() => {
		loadPinned();
	}, [loadPinned]);

	useEffect(() => {
		if (skipFetch) {
			setPinnedJobs([]);
			setLoading(false);
			setError(null);
		}
	}, [skipFetch]);

	useEffect(() => {
		if (isCompact) {
			setExpanded(defaultExpanded);
		}
	}, [customerId, isCompact, defaultExpanded]);

	const count = pinnedJobs.length;
	const navigateOnClick = !isCompact;

	const openInPhotosNotes = useCallback(
		(job) => {
			if (!job || !customerId || !navigateOnClick) {
				return;
			}
			onEditJob(job);
			history.push(`${CUSTOMERS_PATH}/${customerId}`);
		},
		[customerId, history, onEditJob, navigateOnClick]
	);

	const onPinnedKeyDown = (job) => (e) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			openInPhotosNotes(job);
		}
	};

	const onUnpinClick = (e, job) => {
		e.stopPropagation();
		setJobToUnpin(job);
	};

	const confirmUnpin = useCallback(() => {
		if (!organisationId || !jobToUnpin) {
			return;
		}
		const body = buildJobUpdateBody(
			jobToUnpin,
			customerId,
			organisationId,
			false
		);
		safetyWiseApi
			.post(`${organisationId}/job/createorupdate`, body)
			.then(() => {
				setJobToUnpin(null);
				loadPinned();
			})
			.catch(() => {
				// eslint-disable-next-line no-alert
				window.alert(
					"Could not unpin. Check your connection and try again."
				);
			});
	}, [organisationId, jobToUnpin, customerId, loadPinned]);

	const toggleExpanded = () => setExpanded((prev) => !prev);

	const onBannerKeyDown = (e) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			toggleExpanded();
		}
	};

	const resolvedCount =
		!loading && count > 0
			? count
			: typeof knownPinnedCount === "number" && knownPinnedCount > 0
			? knownPinnedCount
			: count;

	const showCompactLoading =
		isCompact &&
		loading &&
		!error &&
		(typeof knownPinnedCount === "number"
			? knownPinnedCount > 0
			: true);

	const pinnedLabel =
		showCompactLoading &&
		!(typeof knownPinnedCount === "number" && knownPinnedCount > 0)
			? "Loading pinned photos / notes…"
			: formatPinnedLabel(resolvedCount, customerLabel);

	const cardList = (
		<Box
			className={`${classes.cardList} ${
				isCompact ? classes.cardListCompact : ""
			}`}
		>
			{pinnedJobs.map((job) => (
				<PinnedJobCard
					key={job.id}
					job={job}
					classes={classes}
					customerLabel={customerLabel}
					allowUnpin={allowUnpin}
					navigateOnClick={navigateOnClick}
					onUnpinClick={onUnpinClick}
					onOpenInPhotosNotes={openInPhotosNotes}
					onPinnedKeyDown={onPinnedKeyDown}
				/>
			))}
		</Box>
	);

	if (skipFetch || (!loading && count === 0 && !error)) {
		return null;
	}

	if (isCompact && loading && !showCompactLoading) {
		return null;
	}

	const rootClass = isCompact ? classes.rootCompact : classes.root;

	return (
		<div
			className={rootClass}
			data-cy="customer-pinned-section"
			data-variant={variant}
		>
			{showCompactLoading ? (
				<Box
					className={`${classes.banner} ${classes.bannerLoading}`}
					data-cy="customer-pinned-loading"
					aria-busy="true"
				>
					<PushPinIcon
						fontSize="small"
						className={classes.bannerIcon}
					/>
					<Typography
						variant="body2"
						component="span"
						className={classes.bannerText}
					>
						{pinnedLabel}
					</Typography>
					<CircularProgress size={20} className={classes.bannerAction} />
				</Box>
			) : loading ? (
				<Typography className={classes.loadingText}>
					Loading pinned items…
				</Typography>
			) : error ? (
				<Typography className={classes.error}>{error}</Typography>
			) : isCompact ? (
				<>
					<Box
						className={classes.banner}
						onClick={toggleExpanded}
						onKeyDown={onBannerKeyDown}
						role="button"
						tabIndex={0}
						aria-expanded={expanded}
						data-cy="customer-pinned-banner"
					>
						<PushPinIcon
							fontSize="small"
							className={classes.bannerIcon}
						/>
						<Typography
							variant="body2"
							component="span"
							className={classes.bannerText}
						>
							{pinnedLabel}
						</Typography>
						{expanded ? (
							<ExpandLessIcon className={classes.bannerAction} />
						) : (
							<ExpandMoreIcon className={classes.bannerAction} />
						)}
					</Box>
					<Collapse in={expanded}>{cardList}</Collapse>
				</>
			) : (
				cardList
			)}
			{allowUnpin && (
				<StyledModal
					open={!!jobToUnpin}
					title="Unpin item?"
					onClose={() => setJobToUnpin(null)}
					onConfirm={confirmUnpin}
					onConfirmText="Yes, unpin"
				>
					{`Are you sure you want to unpin this from the ${customerLabel.toLowerCase()}? It will no longer appear here.`}
				</StyledModal>
			)}
		</div>
	);
};

CustomerPinnedItems.propTypes = {
	customerId: PropTypes.string.isRequired,
	organisationId: PropTypes.string,
	onEditJob: PropTypes.func.isRequired,
	variant: PropTypes.oneOf(["customer", "compact"]),
	defaultExpanded: PropTypes.bool,
	allowUnpin: PropTypes.bool,
	knownPinnedCount: PropTypes.number,
};

export default connect(null, { onEditJob: onEdit })(CustomerPinnedItems);
